/**
 * The Settings navigation, shared by the Settings page and the billing pages.
 * Which assumption sections exist comes from the experience registry
 * (`spec.settings`); whether this account sees one comes from the server's
 * component grant for it (`settings.home_buyer`, `settings.investor`), the
 * same rule the old workbench applied. Refurbishment is shown with either.
 */
import { useNavigate } from '@tanstack/react-router'
import { useAccess } from '../../api/queries'
import { experiences } from '../../experiences/registry'
import { useExperience } from '../../experiences/useExperience'

export type SettingsSection = 'appearance' | 'refurbishment' | 'homeBuyer' | 'investor' | 'billing'
export type AssumptionSection = 'homeBuyer' | 'investor'

export const SECTIONS: Array<{ id: SettingsSection; label: string; note: string }> = [
  { id: 'appearance', label: 'Appearance', note: 'Theme, skin, photographs' },
  { id: 'refurbishment', label: 'Refurbishment', note: 'Your default works budget' },
  { id: 'homeBuyer', label: 'Home buyer', note: 'Buying, finance and bargain targets' },
  { id: 'investor', label: 'Investor', note: 'Shared assumptions for your models' },
  { id: 'billing', label: 'Billing', note: 'Plan, trial, invoices' },
]

/** The server component that grants each assumption section. */
const SECTION_COMPONENT: Record<AssumptionSection, string> = { homeBuyer: 'settings.home_buyer', investor: 'settings.investor' }

export interface SettingsAccess {
  ready: boolean
  error: string | null
  /** Account may use the product (false: only plans and billing). */
  allowsProduct: boolean
  canHomeBuyer: boolean
  canInvestor: boolean
  canReadDefaults: boolean
  visible: SettingsSection[]
  /** The assumption section matching the active experience, for fallbacks. */
  preferred: AssumptionSection | null
}

export function useSettingsAccess(): SettingsAccess {
  const access = useAccess()
  const experience = useExperience()
  // Sections declared by any experience in the registry, in registry order.
  const declared = [...new Set(Object.values(experiences).flatMap(spec => spec.settings))]
  const granted = (section: AssumptionSection) => declared.includes(section) && (access.isSuccess
    ? access.can(SECTION_COMPONENT[section])
    : !access.isError && experience.available.some(spec => spec.settings.includes(section)))
  const allowsProduct = access.data?.accountAccess?.allowsProduct !== false
  const canHomeBuyer = allowsProduct && granted('homeBuyer')
  const canInvestor = allowsProduct && granted('investor')
  const canReadDefaults = canHomeBuyer || canInvestor
  const visible = SECTIONS.map(s => s.id).filter(id => id === 'appearance' || id === 'billing' || (id === 'refurbishment' ? canReadDefaults : id === 'homeBuyer' ? canHomeBuyer : canInvestor))
  const preferred: AssumptionSection | null = experience.spec.settings.includes('homeBuyer') && canHomeBuyer ? 'homeBuyer' : canInvestor ? 'investor' : canHomeBuyer ? 'homeBuyer' : null
  return { ready: access.isSuccess, error: access.error?.message ?? null, allowsProduct, canHomeBuyer, canInvestor, canReadDefaults, visible, preferred }
}

export function SettingsRail({ current, locked, onSelect }: { current: SettingsSection; locked?: boolean; onSelect?: (section: SettingsSection) => void }) {
  const navigate = useNavigate()
  const { visible } = useSettingsAccess()
  const go = (id: SettingsSection) => {
    if (locked) return
    if (id === 'billing') { void navigate({ to: '/settings/billing' }); return }
    if (onSelect) onSelect(id)
    else void navigate({ to: '/settings', search: id === 'appearance' ? {} : { section: id } })
  }
  return (
    <nav className="smenu" aria-label="Settings sections">
      <span className="t-section">Settings</span>
      {SECTIONS.filter(item => visible.includes(item.id)).map(item => (
        <a key={item.id} href={item.id === 'billing' ? '/settings/billing' : `/settings${item.id === 'appearance' ? '' : `?section=${item.id}`}`} className={current === item.id ? 'is-on' : ''} aria-current={current === item.id ? 'page' : undefined} aria-disabled={locked || undefined} onClick={e => { e.preventDefault(); go(item.id) }}>{item.label}</a>
      ))}
    </nav>
  )
}
